import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { formatDate, apiErrorMsg, STATUS_LABELS, STATUS_COLORS, SOURCE_LABELS } from '@/lib/formatters';
import {
  Globe, Upload, Settings, BarChart3, Copy, Check, ArrowUpRight,
  FileUp, AlertTriangle, CheckCircle, ExternalLink, TrendingUp
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from 'recharts';

const PORTAL_COLORS = { '99acres': '#E74C3C', 'Housing.com': '#2ECC71', 'MagicBricks': '#F39C12' };
const BAR_COLORS = ['#E74C3C', '#2ECC71', '#F39C12', '#3498DB'];

export default function PortalLeadsPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('overview');

  return (
    <div className="space-y-6" data-testid="portal-leads-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Portal Lead Aggregation</h1>
        <p className="text-slate-500 text-sm mt-0.5">Capture leads from 99acres, Housing.com, MagicBricks via webhooks or CSV upload</p>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="overview" data-testid="portal-overview-tab"><BarChart3 size={14} className="mr-1" /> Overview</TabsTrigger>
          <TabsTrigger value="import" data-testid="portal-import-tab"><Upload size={14} className="mr-1" /> Import Leads</TabsTrigger>
          <TabsTrigger value="recent" data-testid="portal-recent-tab"><Globe size={14} className="mr-1" /> Recent Portal Leads</TabsTrigger>
          {user?.role === 'director' && (
            <TabsTrigger value="setup" data-testid="portal-setup-tab"><Settings size={14} className="mr-1" /> Webhook Setup</TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="overview"><OverviewTab /></TabsContent>
        <TabsContent value="import"><ImportTab /></TabsContent>
        <TabsContent value="recent"><RecentLeadsTab /></TabsContent>
        {user?.role === 'director' && <TabsContent value="setup"><SetupTab /></TabsContent>}
      </Tabs>
    </div>
  );
}

function OverviewTab() {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/portals/stats').then(r => { setStats(r.data.stats); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const totalLeads = stats.reduce((s, p) => s + p.total, 0);

  return (
    <div className="space-y-6">
      {/* Portal Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {loading ? [1,2,3].map(i => <div key={i} className="h-28 bg-white rounded-xl border animate-pulse" />) : (
          <>
            {[
              { name: '99acres', logo: '99', color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200' },
              { name: 'Housing.com', logo: 'H', color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200' },
              { name: 'MagicBricks', logo: 'M', color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200' },
            ].map(portal => {
              const s = stats.find(st => st.label === portal.name) || { total: 0, new: 0, booked: 0, conversion: 0 };
              return (
                <Card key={portal.name} className={`border ${portal.border}`} data-testid={`portal-stat-${portal.name}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className={`w-9 h-9 rounded-lg ${portal.bg} flex items-center justify-center`}>
                          <span className={`font-extrabold text-sm ${portal.color}`}>{portal.logo}</span>
                        </div>
                        <span className="font-semibold text-slate-800">{portal.name}</span>
                      </div>
                      <Badge variant="outline" className="text-xs font-mono">{s.total} leads</Badge>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div>
                        <p className="text-lg font-bold text-blue-600">{s.new}</p>
                        <p className="text-[10px] text-slate-400 uppercase">New</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-emerald-600">{s.booked}</p>
                        <p className="text-[10px] text-slate-400 uppercase">Booked</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-slate-700">{s.conversion}%</p>
                        <p className="text-[10px] text-slate-400 uppercase">Conv.</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </>
        )}
      </div>

      {/* Chart */}
      {stats.length > 0 && totalLeads > 0 && (
        <Card className="border-slate-200">
          <CardHeader className="pb-2"><CardTitle className="text-base font-semibold">Portal Lead Distribution</CardTitle></CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.filter(s => s.total > 0)} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#64748B' }} />
                  <YAxis tick={{ fontSize: 12, fill: '#64748B' }} />
                  <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #E2E8F0' }} />
                  <Bar dataKey="total" name="Total Leads" radius={[4, 4, 0, 0]}>
                    {stats.filter(s => s.total > 0).map((_, i) => <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {totalLeads === 0 && !loading && (
        <Card className="border-slate-200"><CardContent className="py-12 text-center">
          <Globe size={40} className="mx-auto mb-3 text-slate-300" />
          <p className="text-slate-500 font-medium">No portal leads yet</p>
          <p className="text-sm text-slate-400 mt-1">Upload a CSV from your portal or configure webhooks in the Setup tab</p>
        </CardContent></Card>
      )}
    </div>
  );
}

function ImportTab() {
  const fileRef = useRef(null);
  const [portal, setPortal] = useState('99acres');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const handleUpload = async () => {
    if (!file) return toast.error('Select a file first');
    setUploading(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post(`/portals/import?portal=${portal}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(data);
      if (data.imported > 0) toast.success(`Imported ${data.imported} leads from ${portal}`);
      else toast.warning('No new leads imported');
    } catch (err) { toast.error(apiErrorMsg(err)); }
    finally { setUploading(false); }
  };

  return (
    <div className="max-w-2xl space-y-4">
      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold">Import Leads from Portal</CardTitle>
          <CardDescription>Upload a CSV or Excel file exported from your property portal. SalesOS will auto-map columns.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Select Portal</Label>
            <Select value={portal} onValueChange={setPortal}>
              <SelectTrigger data-testid="import-portal-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="99acres">99acres</SelectItem>
                <SelectItem value="housing">Housing.com</SelectItem>
                <SelectItem value="magicbricks">MagicBricks</SelectItem>
                <SelectItem value="other">Other Portal</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Upload File (CSV / Excel)</Label>
            <div
              className="mt-1 border-2 border-dashed border-slate-300 rounded-xl p-8 text-center cursor-pointer hover:border-emerald-400 hover:bg-emerald-50/30 transition-colors"
              onClick={() => fileRef.current?.click()}
              data-testid="import-drop-zone"
            >
              <FileUp size={32} className="mx-auto mb-2 text-slate-400" />
              {file ? (
                <p className="text-sm font-medium text-slate-700">{file.name} <span className="text-slate-400">({(file.size / 1024).toFixed(0)} KB)</span></p>
              ) : (
                <>
                  <p className="text-sm text-slate-500">Click to select a CSV or Excel file</p>
                  <p className="text-xs text-slate-400 mt-1">Required columns: Name, Phone. Optional: Email, City, Project, Configuration, Remarks</p>
                </>
              )}
              <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={e => setFile(e.target.files?.[0] || null)} data-testid="import-file-input" />
            </div>
          </div>

          <Button onClick={handleUpload} disabled={uploading || !file} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="import-upload-btn">
            <Upload size={16} /> {uploading ? 'Importing...' : 'Import Leads'}
          </Button>

          {result && (
            <Card className={`border ${result.imported > 0 ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  {result.imported > 0 ? <CheckCircle size={18} className="text-emerald-600" /> : <AlertTriangle size={18} className="text-amber-600" />}
                  <span className="font-semibold text-sm">{result.imported > 0 ? 'Import Complete' : 'Import Summary'}</span>
                </div>
                <div className="grid grid-cols-3 gap-3 text-center mt-2">
                  <div><p className="text-xl font-bold text-emerald-700">{result.imported}</p><p className="text-xs text-slate-500">Imported</p></div>
                  <div><p className="text-xl font-bold text-amber-600">{result.duplicates}</p><p className="text-xs text-slate-500">Duplicates</p></div>
                  <div><p className="text-xl font-bold text-slate-600">{result.total_rows}</p><p className="text-xs text-slate-500">Total Rows</p></div>
                </div>
                {result.errors?.length > 0 && (
                  <div className="mt-3 max-h-32 overflow-y-auto">
                    {result.errors.slice(0, 10).map((e, i) => (
                      <p key={i} className="text-xs text-red-600">{e}</p>
                    ))}
                    {result.errors.length > 10 && <p className="text-xs text-slate-400">...and {result.errors.length - 10} more</p>}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>

      {/* Column mapping reference */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Auto Column Mapping</CardTitle></CardHeader>
        <CardContent className="text-sm text-slate-600">
          <p className="mb-2">SalesOS automatically detects these column names from any portal export:</p>
          <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
            <div><strong>Name:</strong> name, Full Name, contact_name, senderName, buyerName</div>
            <div><strong>Phone:</strong> phone, Mobile, contact_phone, senderMobile, buyerPhone</div>
            <div><strong>Email:</strong> email, contact_email, senderEmail, buyerEmail</div>
            <div><strong>Project:</strong> project, project_name, propertyName, property_title</div>
            <div><strong>Config:</strong> configuration, BHK, bedrooms, property_type</div>
            <div><strong>City:</strong> city, location, locality</div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function RecentLeadsTab() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/leads', { params: { limit: 50, source: '' } }).then(r => {
      const portalLeads = r.data.leads.filter(l =>
        ['99acres', 'housing_com', 'magicbricks'].includes(l.source) || l.portal_source
      );
      setLeads(portalLeads);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500">{leads.length} leads from property portals</p>
      <div className="table-container bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Lead ID</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Name</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Phone</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Portal</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Project</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Status</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? Array.from({ length: 5 }).map((_, i) => (
              <TableRow key={i}>{Array.from({ length: 7 }).map((_, j) => <TableCell key={j}><div className="h-4 bg-slate-100 rounded animate-pulse" /></TableCell>)}</TableRow>
            )) : leads.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-12 text-slate-400">
                <Globe size={32} className="mx-auto mb-2 text-slate-300" />
                No portal leads yet. Import from CSV or configure webhooks.
              </TableCell></TableRow>
            ) : leads.map(l => (
              <TableRow key={l.lead_id} data-testid={`portal-lead-${l.lead_id}`}>
                <TableCell className="font-mono text-xs text-slate-500">{l.lead_id}</TableCell>
                <TableCell className="font-semibold text-sm">{l.name}</TableCell>
                <TableCell className="text-sm text-slate-600">{l.phone}</TableCell>
                <TableCell>
                  <Badge className="text-xs" style={{ backgroundColor: PORTAL_COLORS[l.portal_source || SOURCE_LABELS[l.source]] + '20', color: PORTAL_COLORS[l.portal_source || SOURCE_LABELS[l.source]] || '#64748B', border: `1px solid ${PORTAL_COLORS[l.portal_source || SOURCE_LABELS[l.source]] || '#E2E8F0'}40` }}>
                    {l.portal_source || SOURCE_LABELS[l.source] || l.source}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm">{l.preferred_project_name || '-'}</TableCell>
                <TableCell>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[l.status] || 'bg-slate-100'}`}>
                    {STATUS_LABELS[l.status] || l.status}
                  </span>
                </TableCell>
                <TableCell className="text-xs text-slate-500">{formatDate(l.created_at)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function SetupTab() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState('');

  useEffect(() => {
    api.get('/portals/config').then(r => { setConfig(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const copyUrl = (url, label) => {
    navigator.clipboard.writeText(url);
    setCopied(label);
    toast.success('Webhook URL copied');
    setTimeout(() => setCopied(''), 2000);
  };

  if (loading) return <div className="text-center py-8 text-slate-400">Loading...</div>;

  const portals = [
    { key: '99acres', name: '99acres', color: 'border-red-200', bg: 'bg-red-50', logo: '99', logoColor: 'text-red-600',
      instructions: 'Go to 99acres Seller Dashboard → Lead Settings → Webhook/API → Paste the URL below as your lead push endpoint.' },
    { key: 'housing', name: 'Housing.com', color: 'border-emerald-200', bg: 'bg-emerald-50', logo: 'H', logoColor: 'text-emerald-600',
      instructions: 'Go to Housing.com Builder Dashboard → Lead Integration → Add Webhook → Paste the URL below.' },
    { key: 'magicbricks', name: 'MagicBricks', color: 'border-amber-200', bg: 'bg-amber-50', logo: 'M', logoColor: 'text-amber-600',
      instructions: 'Go to MagicBricks Dashboard → CRM Integration → Webhook Setup → Paste the URL below.' },
  ];

  return (
    <div className="max-w-2xl space-y-4">
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4 text-sm text-blue-800">
          <p className="font-semibold mb-1">How Portal Webhooks Work</p>
          <p>When a customer enquires on a portal, the portal sends the lead details to your unique webhook URL below. SalesOS automatically creates a new lead, tags the source, and places it in the unassigned queue for your manager to distribute.</p>
        </CardContent>
      </Card>

      {portals.map(portal => (
        <Card key={portal.key} className={`border ${portal.color}`} data-testid={`portal-webhook-${portal.key}`}>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-lg ${portal.bg} flex items-center justify-center`}>
                <span className={`font-extrabold text-xs ${portal.logoColor}`}>{portal.logo}</span>
              </div>
              <div>
                <CardTitle className="text-sm font-semibold">{portal.name}</CardTitle>
                <CardDescription className="text-xs">{portal.instructions}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-white p-2.5 rounded-lg text-xs font-mono border border-slate-200 truncate">
                {config?.webhooks?.[portal.key]}
              </code>
              <Button variant="outline" size="sm" onClick={() => copyUrl(config?.webhooks?.[portal.key], portal.key)} data-testid={`copy-webhook-${portal.key}`}>
                {copied === portal.key ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
              </Button>
            </div>
            <p className="text-[10px] text-slate-400 mt-1.5">Accepts POST requests with JSON or form-data. Required fields: name + phone.</p>
          </CardContent>
        </Card>
      ))}

      <Card className="border-slate-200">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Testing Your Webhook</CardTitle></CardHeader>
        <CardContent className="text-sm text-slate-600 space-y-1">
          <p>Send a test POST request to verify your webhook is working:</p>
          <pre className="bg-slate-900 text-emerald-400 p-3 rounded-lg text-xs overflow-x-auto mt-2">
{`curl -X POST "${config?.webhooks?.['99acres']}" \\
  -H "Content-Type: application/json" \\
  -d '{"name":"Test Lead","phone":"+919999900000","project":"Test Project"}'`}
          </pre>
          <p className="text-xs text-slate-400 mt-2">A successful response returns: {`{"status":"ok","created":true,"lead_id":"VIR-XXXX"}`}</p>
        </CardContent>
      </Card>
    </div>
  );
}
